// Implements: F-UIX-018, F-USG-005, F-USG-006
/**
 * The client of "Consumo por ámbito" (UIX.5): an organization's or a team's consumption and
 * per-mockup cost list (S11a-1, S11a-2), and the organization's teams to name the rows.
 *
 * Every address is built from the target the caller passes, ids escaped, so a request only
 * ever names the organization or team on screen. The server decides access before it reads
 * any id and answers every refused reader with the `404` of a path that does not exist. The
 * reports are read over the server's default range (the last 366 days, in the zone of the
 * month window), which the answers echo as `from` and `to`.
 */

import { API_BASE } from "../../../shared/api/apiBase";
import { httpRequest } from "../../../shared/api/httpClient";
import type {
  ConsumptionGrouping,
  OrganizationConsumptionView,
  ReportTarget,
  ScopeMockupsView,
} from "../../../types/reports";

/** One team as `GET /organizations/{organization_id}/teams` lists it (org admin only). */
export interface TeamSummaryView {
  readonly id: string;
  readonly organization_id: string;
  readonly name: string;
  readonly created_at: string;
}

export interface TeamListView {
  readonly teams: readonly TeamSummaryView[];
}

/** The report base of a target: the organization's, or its team's under it. */
export function reportBaseOf(target: ReportTarget): string {
  const organization = `${API_BASE}/reports/organizations/${encodeURIComponent(target.organizationId)}`;
  return target.kind === "organization"
    ? organization
    : `${organization}/teams/${encodeURIComponent(target.teamId)}`;
}

/** A read whose body is missing is a broken answer, not an empty one. */
function required<T>(body: T | undefined, path: string): T {
  if (body === undefined) throw new Error(`empty answer from ${path}`);
  return body;
}

export async function fetchScopeConsumption(
  target: ReportTarget,
  grouping: ConsumptionGrouping,
  signal?: AbortSignal,
): Promise<OrganizationConsumptionView> {
  const path = `${reportBaseOf(target)}/consumption?scope=${grouping}`;
  return required(await httpRequest<OrganizationConsumptionView>(path, { signal }), path);
}

export async function fetchScopeMockups(target: ReportTarget, signal?: AbortSignal): Promise<ScopeMockupsView> {
  const path = `${reportBaseOf(target)}/mockups`;
  return required(await httpRequest<ScopeMockupsView>(path, { signal }), path);
}

export async function fetchOrganizationTeams(organizationId: string, signal?: AbortSignal): Promise<TeamListView> {
  const path = `${API_BASE}/organizations/${encodeURIComponent(organizationId)}/teams`;
  return required(await httpRequest<TeamListView>(path, { signal }), path);
}
